import {useEffect, useState} from 'react';
import {Platform} from 'react-native';
import {useSelector} from 'react-redux';

import {RootState} from '/redux/reducer';
import {
  showProfileSaveSuccessToast,
  showSocialLinksSaveErrorToast,
  showSocialLinksSaveSuccessToast,
} from '/util/toast.util';

import {
  BusinessProfileApiPayload,
  BusinessProfileApiResponse,
  EditFormPayload,
  SocialLink,
  SocialMediaApiPayload,
  SocialMediaApiResponse,
  ViewData,
} from '../ManageMySite.types';

import {UploadBusinessProfileImagePayload} from './ManageMySiteContainer.types';
import {
  buildPatchPayload,
  mapEditPayloadToViewData,
  mapProfileResponseToViewData,
} from './ManageMySiteContainer.util';

const EMPTY_VIEW_DATA: ViewData = {
  avatarUrl: '',
  webAlias: '',
  baseWebsiteUrl: '',
  firstName: '',
  lastName: '',
  displayName: '',
  email: '',
  phonePrimary: '',
  phoneSecondary: '',
  country: 'US',
  street: '',
  city: '',
  state: '',
  zipcode: '',
  about: '',
  transformationBeforeUrl: '',
  transformationAfterUrl: '',
};

type SavingSection = 'businessProfile' | 'socialLinks' | null;

type UseManageMySiteContainerProps = {
  businessProfile?: BusinessProfileApiResponse;
  hasBusinessProfile?: boolean;
  getBusinessProfile?: () => Promise<void> | void;
  patchBusinessProfile?: (
    payload: Partial<BusinessProfileApiPayload>,
  ) => Promise<void>;

  socialMedia?: SocialMediaApiResponse;
  hasSocialMedia?: boolean;
  getSocialMedia?: () => Promise<void> | void;
  patchSocialMedia?: (payload: SocialMediaApiPayload) => Promise<void>;
  uploadBusinessProfileImage?: (
    payload: UploadBusinessProfileImagePayload,
  ) => Promise<any>;
};

export const useManageMySiteContainer = ({
  businessProfile,
  hasBusinessProfile,
  getBusinessProfile,
  patchBusinessProfile,
  socialMedia,
  hasSocialMedia,
  getSocialMedia,
  patchSocialMedia,
  uploadBusinessProfileImage,
}: UseManageMySiteContainerProps) => {
  const [mode, setMode] = useState<'view' | 'edit' | 'social'>('view');
  const [profileData, setProfileData] = useState<ViewData>(EMPTY_VIEW_DATA);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [savingSection, setSavingSection] = useState<SavingSection>(null);

  const webImageOverrides = useSelector(
    (state: RootState) => state.webImageOverride,
  );

  useEffect(() => {
    if (!hasBusinessProfile) {
      getBusinessProfile?.();
    }

    if (!hasSocialMedia) {
      getSocialMedia?.();
    }
  }, [getBusinessProfile, getSocialMedia, hasBusinessProfile, hasSocialMedia]);

  useEffect(() => {
    if (businessProfile) {
      const mappedData = mapProfileResponseToViewData(businessProfile);

      if (Platform.OS === 'web') {
        setProfileData({
          ...mappedData,
          avatarUrl: webImageOverrides.avatar || mappedData.avatarUrl,
          transformationBeforeUrl:
            webImageOverrides.before || mappedData.transformationBeforeUrl,
          transformationAfterUrl:
            webImageOverrides.after || mappedData.transformationAfterUrl,
        });
      } else {
        setProfileData(mappedData);
      }
    }
  }, [businessProfile, webImageOverrides]);

  useEffect(() => {
    if (socialMedia) {
      setSocialLinks(Array.isArray(socialMedia) ? socialMedia : []);
    }
  }, [socialMedia]);

  const handlePhotoUpload = async (
    type: 'avatar' | 'before' | 'after',
    image: {
      uri: string;
      mimeType?: string;
      base64?: string;
    },
  ): Promise<any> => {
    const fieldName =
      type === 'avatar'
        ? 'avatarUrl'
        : type === 'before'
        ? 'transformationBeforeUrl'
        : 'transformationAfterUrl';

    const previousUrl =
      type === 'avatar'
        ? businessProfile?.avatarUrl || ''
        : type === 'before'
        ? businessProfile?.beforeImageUrl || ''
        : businessProfile?.afterImageUrl || '';

    const normalizedMimeType = image.mimeType || 'image/jpeg';
    const normalizedBase64 = image.base64 || '';
    const localPreviewUri = image.uri || '';

    try {
      if (!localPreviewUri) {
        throw new Error(`Missing image uri for ${type} upload`);
      }

      if (!normalizedBase64) {
        throw new Error(`Missing base64 for ${type} upload`);
      }

      setProfileData(prev => ({
        ...prev,
        [fieldName]: localPreviewUri,
      }));

      const response = await uploadBusinessProfileImage?.({
        imageType: type,
        base64: normalizedBase64,
        mimeType: normalizedMimeType,
        uri: localPreviewUri,
      });

      const uploadedUrl = response?.imageUrl || localPreviewUri;

      setProfileData(prev => ({
        ...prev,
        [fieldName]: uploadedUrl,
      }));

      // getBusinessProfile?.();

      return response;
    } catch (e) {
      setProfileData(prev => ({
        ...prev,
        [fieldName]: previousUrl,
      }));

      throw e;
    }
  };

  const getApiErrorBody = (error: any) => {
    try {
      return error?.meta?.body ? JSON.parse(error.meta.body) : {};
    } catch {
      return {};
    }
  };

  const handleSaveProfile = async (payload: EditFormPayload): Promise<void> => {
    try {
      const patchPayload = buildPatchPayload(profileData, payload);

      if (Object.keys(patchPayload).length === 0) {
        setMode('view');
        showProfileSaveSuccessToast();
        return;
      }

      await patchBusinessProfile?.(patchPayload);

      setProfileData(prev => mapEditPayloadToViewData(prev, payload));
      setMode('view');
      setSavingSection('businessProfile');
      // showProfileSaveSuccessToast();

      try {
        await getBusinessProfile?.();
      } finally {
        setSavingSection(null);
      }
    } catch (e: any) {
      setSavingSection(null);
      getBusinessProfile?.();

      const body = getApiErrorBody(e);

      const message =
        body?.message ||
        e?.message ||
        'Failed to save profile changes. Please try again.';

      const field = body?.field;

      if (field) {
        throw {
          field,
          message,
        };
      }

      // showProfileSaveErrorToast();
      throw e;
    }
  };

  const handleSaveSocialLinks = async (
    payload: SocialMediaApiPayload,
  ): Promise<void> => {
    try {
      if (!payload.socialNetworks.length) {
        setMode('view');
        showSocialLinksSaveSuccessToast();
        return;
      }

      await patchSocialMedia?.(payload);

      setSocialLinks(payload.socialNetworks as SocialLink[]);
      setMode('view');
      setSavingSection('socialLinks');
      showSocialLinksSaveSuccessToast();

      try {
        await getSocialMedia?.();
      } finally {
        setSavingSection(null);
      }
    } catch (e) {
      setSavingSection(null);
      getSocialMedia?.();
      showSocialLinksSaveErrorToast();
      throw e;
    }
  };

  return {
    mode,
    setMode,
    profileData,
    socialLinks,
    savingSection,
    handlePhotoUpload,
    handleSaveProfile,
    handleSaveSocialLinks,
  };
};
